import * as UI from "@iyk/ui"

const BASE_URL = "https://krost.iyk.app"

const DEFAULT_IMAGE_URL = "https://assets.iyk.app/krost/og-image.png"

const DEFAULT_META_TITLE = "KROST — Referrals Powered by IYK"

const DEFAULT_DESCRIPTION =
  "An all-in-one system to onboard influencers, generate referral links, and manage payouts. Powered by IYK."

function isSupportedImage(url: string) {
  return Object.values(UI.acceptImagesFileType.accept)
    .flatMap((ext) => ext)
    .some((ext) => url.endsWith(ext))
}

function getImageUrl(imageUrl: OpenGraphMetadata["imageUrl"]) {
  return imageUrl && isSupportedImage(imageUrl) ? imageUrl : DEFAULT_IMAGE_URL
}

function getTypeSpecificProperties(metadata: OpenGraphMetadata): OpenGraphProperty[] {
  switch (metadata.type) {
    case "profile":
      return [{ property: "profile:username", content: metadata.username }]
    case "website":
      return []
  }
}

/** Generate the Open Graph properties for a given metadata */
function generateProperties(metadata: OpenGraphMetadata): OpenGraphProperty[] {
  return [
    { property: "og:site_name", content: "KROST" },
    { property: "og:title", content: metadata.title ?? DEFAULT_META_TITLE },
    { property: "og:image", content: getImageUrl(metadata.imageUrl) },
    {
      property: "og:description",
      content: metadata.description ?? DEFAULT_DESCRIPTION,
    },
    { property: "og:url", content: `${BASE_URL}${metadata.pathname}` },
    { property: "og:type", content: metadata.type },
    {
      property: "og:width",
      content: "1200",
    },
    {
      property: "og:height",
      content: "630",
    },
    {
      property: "twitter:card",
      content: "summary_large_image",
    },
    {
      property: "twitter:title",
      content: metadata.title ?? DEFAULT_META_TITLE,
    },
    {
      property: "twitter:description",
      content: metadata.description ?? DEFAULT_DESCRIPTION,
    },
    {
      property: "twitter:image",
      content: getImageUrl(metadata.imageUrl),
    },
    {
      property: "twitter:width",
      content: "1200",
    },
    {
      property: "twitter:height",
      content: "630",
    },
    ...getTypeSpecificProperties(metadata),
  ]
}

export const OpenGraph = {
  generateProperties,
  DEFAULT_META_TITLE,
}

type BaseMetadata = {
  title?: string
  pathname: string
  imageUrl?: string | null
  description?: string | null
}

type ProfileMetadata = BaseMetadata & {
  type: "profile"
  username: string
}

type WebsiteMetadata = BaseMetadata & {
  type: "website"
}

type OpenGraphMetadata = ProfileMetadata | WebsiteMetadata

type OpenGraphProperty = {
  property: string
  content: string
}
